import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Col,
  Progress,
  Row,
  Table,
  Tooltip,
  Typography,
} from "antd";
import {
  BarcodeOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUiState } from "../state";
import { textMap } from "../i18n";
import {
  fetchBillingSummary,
  fetchCurrentUser,
  fetchDashboardOverview,
  type BillingSummary,
  type CurrentUser,
  type DashboardOverview,
} from "../services/compliance";

const { Title, Paragraph } = Typography;

export function DashboardPage() {
  const { lang, themeMode } = useUiState();
  const navigate = useNavigate();
  const t = textMap[lang];
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      fetchBillingSummary(),
      fetchDashboardOverview(),
      fetchCurrentUser(),
    ]).then(([billing, dash, me]) => {
      if (!mounted) return;
      setSummary(billing);
      setOverview(dash);
      setCurrentUser(me);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const renewText = useMemo(() => {
    if (summary?.renew_at) {
      return new Date(summary.renew_at).toLocaleDateString(
        lang === "zh" ? "zh-CN" : "en-US",
      );
    }
    return "-";
  }, [lang, summary]);

  const columns = [
    { title: t.recentColumns[0], dataIndex: "time", key: "time" },
    { title: t.recentColumns[1], dataIndex: "product", key: "product" },
    {
      title: t.recentColumns[2],
      dataIndex: "risk",
      key: "risk",
      render: (value: string) => {
        const lower = String(value).toLowerCase();
        const isHigh = lower === "high";
        return (
          <span
            className={`dashboard-risk-tag ${isHigh ? "high" : lower === "medium" ? "medium" : "low"}`}
          >
            {String(value).toUpperCase()}
          </span>
        );
      },
    },
    {
      title: t.recentColumns[3],
      dataIndex: "status",
      key: "status",
      render: (value: string) => {
        const done = value === "done";
        return (
          <div className="dashboard-status-cell">
            <span className={`dashboard-status-dot ${done ? "done" : "running"}`} />
            {done
              ? lang === "zh"
                ? "已完成"
                : "Completed"
              : lang === "zh"
                ? "处理中"
                : "Scanning"}
          </div>
        );
      },
    },
  ];

  const dataSource = (overview?.recent_scans ?? []).map((row) => ({
    key: row.id,
    time: new Date(row.time).toLocaleTimeString(lang === "zh" ? "zh-CN" : "en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
    product: row.product,
    risk: row.risk,
    status: row.status,
  }));

  const usageMeter = useMemo(() => {
    if (typeof overview?.api_usage_ratio === "number") {
      return {
        percent: Math.round(overview.api_usage_ratio * 100),
        source: "api" as const,
      };
    }
    const q = summary?.month_scan_quota;
    const used = summary?.month_used_scans ?? 0;
    if (typeof q === "number" && q > 0) {
      return {
        percent: Math.round((used / q) * 100),
        source: "quota" as const,
      };
    }
    return { percent: 0, source: "none" as const };
  }, [overview?.api_usage_ratio, summary?.month_scan_quota, summary?.month_used_scans]);

  const usageLabel =
    usageMeter.source === "api"
      ? t.dashboardUsageMeterApi
      : usageMeter.source === "quota"
        ? t.dashboardUsageMeterQuota
        : t.dashboardUsageMeterGeneric;

  const usageHint =
    usageMeter.source === "api"
      ? t.dashboardUsageMeterHintApi
      : usageMeter.source === "quota"
        ? t.dashboardUsageMeterHintQuota
        : t.dashboardUsageMeterHintNone;

  const roleText = currentUser?.role?.trim()
    ? currentUser.role
    : lang === "zh"
      ? "用户"
      : "Member";

  const accountEmail =
    currentUser?.email?.trim() || null;

  const planBadge = (summary?.plan?.code ?? "FREE").toUpperCase().replace(/_/g, " ");

  return (
    <div className="dashboard-sentinel">
      <section className="dashboard-hero">
        <Title level={2} className="dashboard-title">
          {t.dashboardTitle}
        </Title>
        <Paragraph className="dashboard-subtitle">{t.dashboardDesc}</Paragraph>
      </section>

      <Row gutter={[24, 24]} className="dashboard-kpi-row">
        <Col xs={24} md={8}>
          <Card className="dashboard-glass-card dashboard-kpi-card" bordered={false}>
            <div className="dashboard-kpi-head">
              <span className="dashboard-kpi-label">{t.statTodayScans}</span>
              <BarcodeOutlined className="dashboard-kpi-icon primary" />
            </div>
            <div className="dashboard-kpi-value">
              <span className="dashboard-kpi-num">{(overview?.scans_today ?? 0).toLocaleString()}</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="dashboard-glass-card dashboard-kpi-card" bordered={false}>
            <div className="dashboard-kpi-head">
              <span className="dashboard-kpi-label">{t.statRiskBlocks}</span>
              <SafetyOutlined className="dashboard-kpi-icon error" />
            </div>
            <div className="dashboard-kpi-value">
              <span className="dashboard-kpi-num">
                {overview?.high_risk_blocks ?? 0}
              </span>
              <span className="dashboard-kpi-meta">
                {lang === "zh" ? "较上一小时" : "vs last hour"}
              </span>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="dashboard-glass-card dashboard-kpi-card" bordered={false}>
            <div className="dashboard-kpi-head">
              <span className="dashboard-kpi-label">{t.statAvgScore}</span>
              <CheckCircleOutlined className="dashboard-kpi-icon tertiary" />
            </div>
            <span className="dashboard-kpi-num">{(overview?.avg_score ?? 0).toFixed(1)}</span>
            <div className="dashboard-kpi-progress">
              <div
                className="dashboard-kpi-progress-bar"
                style={{
                  width: `${Math.min(100, overview?.avg_score ?? 0)}%`,
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} xl={16}>
          <Card
            className="dashboard-glass-card dashboard-table-card"
            bordered={false}
          >
            <div className="dashboard-table-head">
              <Title level={5} className="dashboard-table-title">
                {lang === "zh" ? "最近审核" : "Recent reviews"}
              </Title>
              <Button type="link" size="small" onClick={() => navigate("/chat")}>
                {lang === "zh" ? "查看全部" : "View all"}
              </Button>
            </div>
            <Table
              columns={columns}
              dataSource={dataSource}
              size="middle"
              pagination={false}
              className="dashboard-table"
              locale={{
                emptyText:
                  lang === "zh"
                    ? "队列中暂无更多记录"
                    : "No further records in queue",
              }}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card className="dashboard-glass-card dashboard-profile-card" bordered={false}>
            <div className="dashboard-profile-gradient" />
            <div className="dashboard-profile-head">
              <Title level={5} className="dashboard-profile-title">
                {t.dashboardMyAccount}
              </Title>
              <span className="dashboard-profile-badge">{planBadge}</span>
            </div>
            <div className="dashboard-profile-user">
              <div className="dashboard-profile-avatar-wrap">
                <Avatar
                  size={96}
                  style={{
                    backgroundColor: themeMode === "dark" ? "#1e3a5f" : "#003d9b",
                    fontSize: 36,
                    fontWeight: 700,
                  }}
                >
                  {(accountEmail?.[0] ?? "?").toUpperCase()}
                </Avatar>
              </div>
              <h4 className="dashboard-profile-name dashboard-profile-account-email">
                {accountEmail ?? t.dashboardAccountUnavailable}
              </h4>
              <p className="dashboard-profile-email dashboard-profile-role-line">
                {t.dashboardFieldRole}: {roleText}
              </p>
            </div>
            <div className="dashboard-profile-meta">
              {currentUser?.id ? (
                <div className="dashboard-profile-meta-row dashboard-profile-meta-row--stack">
                  <span className="dashboard-profile-meta-label">
                    {t.dashboardUserId}
                  </span>
                  <span className="dashboard-profile-meta-value dashboard-profile-user-id">
                    {currentUser.id}
                  </span>
                </div>
              ) : null}
              <div className="dashboard-profile-meta-row">
                <span className="dashboard-profile-meta-label">
                  {t.dashboardFieldRole}
                </span>
                <span className="dashboard-profile-meta-value">{roleText}</span>
              </div>
              <div className="dashboard-profile-meta-row">
                <span className="dashboard-profile-meta-label">
                  {lang === "zh" ? "续费日期" : "Renewal"}
                </span>
                <span className="dashboard-profile-meta-value">{renewText}</span>
              </div>
              <div className="dashboard-profile-meta-row">
                <Tooltip title={usageHint}>
                  <div className="dashboard-profile-usage-head">
                    <span className="dashboard-profile-meta-label">{usageLabel}</span>
                    <span className="dashboard-profile-usage-pct">{usageMeter.percent}%</span>
                  </div>
                  <Progress
                    percent={usageMeter.percent}
                    showInfo={false}
                    strokeColor={themeMode === "dark" ? "#38bdf8" : "#003d9b"}
                  />
                </Tooltip>
              </div>
            </div>
            <Button
              block
              className="dashboard-profile-manage-btn"
              onClick={() => navigate("/app/billing")}
            >
              {lang === "zh" ? "管理订阅" : "Manage Subscription"}
            </Button>
          </Card>

          {overview?.system_alert ? (
            <div className="dashboard-system-alert">
              <span className="dashboard-alert-icon">ℹ</span>
              <div>
                <p className="dashboard-alert-title">{overview.system_alert.title}</p>
                <p className="dashboard-alert-msg">{overview.system_alert.message}</p>
              </div>
            </div>
          ) : null}
        </Col>
      </Row>
    </div>
  );
}
